import React from 'react'
import cx from 'classnames'
import parse, { domToReact, attributesToProps } from 'html-react-parser'
import { camelCase, trim } from 'libraries/lodash'
import SmartFcFaqReferenceLinkContainer from 'redux/containers/components/SmartFcFaqReferenceLinkContainer'
import SmartFcCrossReferenceLink from 'components/SmartFcCrossReferenceLink'
import SmartFcNewsReferenceLink from 'components/SmartFcNewsReferenceLink'
import SmartFcDocumentReferenceLink from 'components/SmartFcDocumentReferenceLink'
import SmartFcLegislationReference from 'components/SmartFcLegislationReference'
import { ALL_AOLS, AOL_CORPORATE } from 'constants/aol/aol'
import { NEWS_HEADER_AOL_COLOURS, QA_CASE_REF_LINKS } from 'constants/feature-flags'

export const styleStringToObject = (style) => {
  const trimmed = trim(style)

  return (
    trimmed.split(';').filter(s => s.length)
      .reduce((a, b) => {
        const keyValue = b.split(':')
        a[camelCase(keyValue[0])] = trim(keyValue[1])
        return a
      }, {})
  )
}

const WHITE_LIKE_COLOR = /^\s*(none|initial|inherit|unset|#fff(?:fff)?|white|transparent|rgba?\(\s*255\s*,\s*255\s*,\s*255)/i
const hasCustomBackground = ({ background, backgroundColor }) =>
  [background, backgroundColor].some(value => Boolean(value) && !WHITE_LIKE_COLOR.test(value))

const withAolHeaderStyle = (props, aol) => {
  const style = props.style || {}
  const isHeaderCell = hasCustomBackground(style)

  const excludedKeys = new Set(['background', 'backgroundColor', ...(isHeaderCell ? ['color'] : [])])
  const borderlessStyle = Object.keys(style).reduce((acc, key) => {
    if (key.toLowerCase().startsWith('border') || excludedKeys.has(key)) return acc
    acc[key] = style[key]
    return acc
  }, {})

  if (!isHeaderCell) {
    return { ...props, style: borderlessStyle }
  }

  const resolvedAol = ALL_AOLS.includes(aol) ? aol : AOL_CORPORATE

  return {
    ...props,
    style: borderlessStyle,
    className: cx(props.className, `bg-aol-${resolvedAol}`, 'white')
  }
}

const createOptions = (aol, caseRefMap = {}) => {
  const options = {
    replace: (domNode) => {
      const { attribs, name, children } = domNode

      if (attribs && attribs.class === 'fc-legislation') {
        return (
          <SmartFcLegislationReference {...{ attribs }}>
            {domToReact(children)}
          </SmartFcLegislationReference>
        )
      }

      if (attribs && attribs.class === 'fc-cross-reference') {
        return (
          <SmartFcCrossReferenceLink {...{ attribs }}>
            {domToReact(children)}
          </SmartFcCrossReferenceLink>
        )
      }

      if (attribs && attribs.class === 'fc-news-reference') {
        return (
          <SmartFcNewsReferenceLink {...{ attribs }}>
            {domToReact(children)}
          </SmartFcNewsReferenceLink>
        )
      }

      if (attribs && attribs.class === 'fc-document-reference') {
        return (
          <SmartFcDocumentReferenceLink {...{ attribs }}>
            {domToReact(children)}
          </SmartFcDocumentReferenceLink>
        )
      }

      if (attribs && attribs.class === 'fc-faq-anchor') {
        return (
          <SmartFcFaqReferenceLinkContainer {...{ attribs }}>
            {domToReact(children)}
          </SmartFcFaqReferenceLinkContainer>
        )
      }

      if (attribs && attribs.class === 'fc-case-ref-external-link') {
        const href = QA_CASE_REF_LINKS && caseRefMap[attribs['data-ncnid']]
        return href
          ? (
            <a href={href} className='hover-underline' target='_blank' rel='noopener noreferrer'>
              {domToReact(children)}
            </a>
            )
          : <>{domToReact(children)}</>
      }
      const styleObj = attribs && styleStringToObject(attribs.style)

      switch (name) {
        case 'head':
        case 'body':
          return (
            <div>
              {domToReact(children, options)}
            </div>
          )
        case 'table':
          return (
            React.createElement(
              'table',
              { style: { ...styleObj, width: '100%' } },
              domToReact(children, { trim: true, ...options })
            )
          )
        case 'td': {
          if (!aol || !NEWS_HEADER_AOL_COLOURS) return null
          const props = withAolHeaderStyle(attributesToProps(attribs), aol)
          return React.createElement('td', props, domToReact(children, options))
        }
        case 'externallink':
          return (
            <a href={attribs.href} className={cx(attribs.class, 'hover-underline')} target='_blank' rel='noopener noreferrer'>
              {domToReact(children, options)}
            </a>
          )
        default:
          return null
      }
    }
  }

  return options
}

export const parseHtmlContent = (dirtyHtml, { aol, caseRefMap } = {}) => parse(dirtyHtml, createOptions(aol, caseRefMap))
